'use client';

import { useId, useRef } from 'react';

import { AnimatePresence, motion } from 'framer-motion';
import { ChevronRight, ScanFace, X } from 'lucide-react';

import { cn } from '@kit/ui/utils';

import {
  AppTabBar,
  Avatar,
  BrandMark,
  CheckBadge,
  DEMO_BRANDS,
  DEMO_COLORS,
  type DemoProps,
  DemoStage,
  DemoToggle,
  EASE_OUT_EXPO,
  FakeQr,
  PhoneFrame,
  Reveal,
  SPRING_SNAPPY,
  TapRipple,
  localize,
  useDemoTimeline,
} from './demo-kit';

/*
 * Member app, padel club: pick a day, pick a peak slot, split the court with
 * three friends, pay a share with Apple Pay, land on the booking pass.
 */

const DURATIONS = [1500, 1000, 1200, 1800, 1700, 3200] as const;

const STEP = {
  pickDay: 1,
  pickSlot: 2,
  sheet: 3,
  pay: 4,
  booked: 5,
} as const;

const COPY = {
  label: {
    en: 'Animated demo of the branded member app: a player books a padel court, splits the payment with three friends and receives a booking pass.',
    ar: 'عرض متحرك لتطبيق الأعضاء بعلامة النادي: لاعب يحجز ملعب بادل ويقسم الدفع مع ثلاثة أصدقاء ويستلم تذكرة الحجز.',
  },
  title: { en: 'Book a court', ar: 'احجز ملعبًا' },
  subtitle: { en: 'Padel · 90 minutes', ar: 'بادل · 90 دقيقة' },
  available: { en: 'Available times', ar: 'الأوقات المتاحة' },
  peak: { en: 'Peak', ar: 'ذروة' },
  courtsFree: { en: 'courts free', ar: 'ملاعب متاحة' },
  lastCourt: { en: 'Last court', ar: 'آخر ملعب' },
  court: { en: 'Court 3 · Panoramic', ar: 'الملعب 3 · بانورامي' },
  when: { en: 'Thu 12 · 8:00 to 9:30 PM', ar: 'الخميس 12 · 8:00 إلى 9:30 م' },
  split: { en: 'Split with players', ar: 'قسّم الدفع مع اللاعبين' },
  splitHint: { en: 'Each player pays their own share', ar: 'يدفع كل لاعب حصته' },
  players: { en: '4 players', ar: '4 لاعبين' },
  continue: { en: 'Continue', ar: 'متابعة' },
  applePay: { en: 'Apple Pay', ar: 'Apple Pay' },
  payTo: { en: 'Pay Northside Padel', ar: 'الدفع إلى نورثسايد بادل' },
  share: { en: 'Your share (1 of 4)', ar: 'حصتك (1 من 4)' },
  confirm: { en: 'Confirm with Face ID', ar: 'التأكيد عبر Face ID' },
  booked: { en: "You're booked", ar: 'تم الحجز' },
  pass: { en: 'Show this pass at reception', ar: 'اعرض التذكرة عند الاستقبال' },
  invites: { en: 'Payment links sent on WhatsApp', ar: 'أُرسلت روابط الدفع عبر واتساب' },
  calendar: { en: 'Add to calendar', ar: 'أضف إلى التقويم' },
  home: { en: 'Home', ar: 'الرئيسية' },
  book: { en: 'Book', ar: 'احجز' },
  bookings: { en: 'Bookings', ar: 'حجوزاتي' },
  profile: { en: 'Profile', ar: 'حسابي' },
} as const;

const DAYS = [
  { en: 'Mon', ar: 'اثنين', date: 9 },
  { en: 'Tue', ar: 'ثلاثاء', date: 10 },
  { en: 'Wed', ar: 'أربعاء', date: 11 },
  { en: 'Thu', ar: 'خميس', date: 12 },
  { en: 'Fri', ar: 'جمعة', date: 13 },
  { en: 'Sat', ar: 'سبت', date: 14 },
  { en: 'Sun', ar: 'أحد', date: 15 },
] as const;

const SLOTS = [
  { en: '5:00 PM', ar: '5:00 م', free: [4, 3] },
  { en: '6:30 PM', ar: '6:30 م', free: [3, 2] },
  { en: '8:00 PM', ar: '8:00 م', free: [2, 1], peak: true },
  { en: '9:30 PM', ar: '9:30 م', free: [4, 2], peak: true },
] as const;

const PLAYERS = [
  { initials: 'SA', tone: 1 },
  { initials: 'MK', tone: 3 },
  { initials: 'RH', tone: 4 },
] as const;

export function CourtBookingDemo({ locale, className, onLoop }: DemoProps) {
  const stageRef = useRef<HTMLDivElement>(null);
  const timeline = useDemoTimeline(stageRef, DURATIONS, { onLoop });
  const layoutScope = useId();
  const copy = localize(COPY, locale);
  const brand = DEMO_BRANDS.padel;
  const step = timeline.step;

  const dayIndex = step >= STEP.pickDay ? 3 : 0;
  const slotIndex = step >= STEP.pickSlot ? 2 : -1;
  const sheetOpen = step === STEP.sheet || step === STEP.pay;

  return (
    <DemoStage ref={stageRef} timeline={timeline} label={copy.label} className={className}>
      <PhoneFrame>
        <div className="absolute inset-0 flex flex-col bg-[#fbfaf8] pt-[44px]">
          <div className="flex items-center justify-between px-5 pb-3 pt-2">
            <div className="flex items-center gap-2.5">
              <BrandMark brand={brand} size={28} />
              <span className="text-[13px] font-semibold text-[#202020]">
                {brand.name[locale]}
              </span>
            </div>
            <Avatar initials="AK" tone={0} size={28} />
          </div>

          <div className="px-5">
            <h3 className="text-[21px] font-bold leading-tight text-[#202020]">
              {copy.title}
            </h3>
            <p className="mt-0.5 text-[12px] text-[#8a857c]">{copy.subtitle}</p>
          </div>

          <div className="mt-4 flex justify-between px-4">
            {DAYS.map((day, index) => {
              const active = index === dayIndex;

              return (
                <div
                  key={day.date}
                  className="relative flex h-[54px] w-[33px] flex-col items-center justify-center rounded-[12px]"
                >
                  {active && (
                    <motion.span
                      layoutId={`${layoutScope}-day`}
                      className="absolute inset-0 rounded-[12px]"
                      style={{ background: brand.accent }}
                      transition={SPRING_SNAPPY}
                    />
                  )}
                  <span
                    className={cn(
                      'relative text-[8.5px] transition-colors duration-300',
                      active ? 'text-white/80' : 'text-[#8a857c]',
                    )}
                  >
                    {day[locale]}
                  </span>
                  <span
                    className={cn(
                      'relative mt-0.5 text-[14px] font-semibold transition-colors duration-300',
                      active ? 'text-white' : 'text-[#202020]',
                    )}
                  >
                    {day.date}
                  </span>
                </div>
              );
            })}
          </div>

          <p className="mt-5 px-5 font-roboto-mono text-[9.5px] uppercase tracking-[0.12em] text-[#8a857c]">
            {copy.available}
          </p>

          <div className="mt-2 space-y-2 px-4">
            {SLOTS.map((slot, index) => {
              const selected = index === slotIndex;
              const free = slot.free[dayIndex === 0 ? 0 : 1]!;

              return (
                <motion.div
                  key={slot.en}
                  className="relative flex h-[50px] items-center justify-between overflow-hidden rounded-[14px] border bg-white px-3.5"
                  initial={false}
                  animate={{
                    borderColor: selected ? brand.accent : DEMO_COLORS.line,
                    backgroundColor: selected ? brand.accentSoft : '#ffffff',
                    scale: selected && step === STEP.pickSlot ? [1, 0.97, 1] : 1,
                  }}
                  transition={{ duration: 0.35 }}
                >
                  <div className="flex items-center gap-2">
                    <span className="text-[14px] font-semibold text-[#202020]">
                      {slot[locale]}
                    </span>
                    {'peak' in slot && slot.peak && (
                      <span className="rounded-full bg-[#fbf0dc] px-1.5 py-[1px] text-[9px] font-medium text-[#9a6412]">
                        {copy.peak}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5">
                    <AnimatePresence mode="popLayout" initial={false}>
                      <motion.span
                        key={`${slot.en}-${free}`}
                        className="whitespace-nowrap text-[10.5px] text-[#8a857c]"
                        initial={{ opacity: 0, y: 6 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -6 }}
                        transition={{ duration: 0.3 }}
                      >
                        {free === 1 ? copy.lastCourt : `${free} ${copy.courtsFree}`}
                      </motion.span>
                    </AnimatePresence>
                    <ChevronRight className="h-3.5 w-3.5 text-[#b5b0a6] rtl:rotate-180" />
                  </div>
                  <TapRipple
                    key={`ripple-${timeline.cycle}`}
                    show={selected && step === STEP.pickSlot}
                    className="left-1/2 top-1/2"
                  />
                </motion.div>
              );
            })}
          </div>

          <AppTabBar
            active={1}
            accent={brand.accent}
            labels={[copy.home, copy.book, copy.bookings, copy.profile]}
          />
        </div>

        {/* Booking sheet */}
        <motion.div
          className="absolute inset-0 z-10 bg-black/25"
          initial={false}
          animate={{ opacity: sheetOpen ? 1 : 0 }}
          transition={{ duration: 0.3 }}
        />
        <motion.div
          className="absolute inset-x-0 bottom-0 z-20 rounded-t-[26px] bg-white px-5 pb-8 pt-3"
          initial={false}
          animate={{ y: sheetOpen ? 0 : '105%' }}
          transition={{ duration: 0.55, ease: EASE_OUT_EXPO }}
        >
          <div className="mx-auto mb-4 h-1 w-9 rounded-full bg-[#dcd7cf]" />
          <p className="text-[16px] font-bold text-[#202020]">{copy.court}</p>
          <p className="mt-0.5 text-[12px] text-[#8a857c]">{copy.when}</p>

          <div className="mt-4 rounded-[14px] border border-[#ebe7e1] p-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[12.5px] font-semibold text-[#202020]">
                  {copy.split}
                </p>
                <p className="text-[10.5px] text-[#8a857c]">{copy.splitHint}</p>
              </div>
              <DemoToggle on={step >= STEP.sheet} accent={brand.accent} delay={0.5} />
            </div>
            <Reveal show={step >= STEP.sheet} delay={0.75} className="mt-3 flex items-center gap-2">
              <div className="flex -space-x-1.5 rtl:space-x-reverse">
                <Avatar initials="AK" tone={0} size={26} />
                {PLAYERS.map((player) => (
                  <Avatar key={player.initials} initials={player.initials} tone={player.tone} size={26} />
                ))}
              </div>
              <span className="text-[11px] font-medium text-[#202020]">{copy.players}</span>
            </Reveal>
          </div>

          <div
            className="mt-4 flex h-11 items-center justify-center rounded-[14px] text-[13px] font-semibold text-white"
            style={{ background: brand.accent }}
          >
            {copy.continue}
          </div>
        </motion.div>

        {/* Apple Pay sheet */}
        <motion.div
          className="absolute inset-x-2 bottom-2 z-30 rounded-[24px] bg-white px-4 pb-5 pt-4 shadow-[0_-10px_40px_rgba(0,0,0,0.18)]"
          initial={false}
          animate={{ y: step === STEP.pay ? 0 : '115%' }}
          transition={{ duration: 0.5, ease: EASE_OUT_EXPO }}
        >
          <div className="flex items-center justify-between">
            <span className="text-[15px] font-semibold text-black">{copy.applePay}</span>
            <X className="h-4 w-4 text-[#8a857c]" />
          </div>
          <div className="mt-3 flex items-center gap-3 rounded-[12px] bg-[#f5f5f7] p-2.5">
            <span className="flex h-7 w-11 items-center justify-center rounded-[5px] bg-gradient-to-br from-[#2d6a4f] to-[#1b4332] font-roboto-mono text-[8px] font-bold text-white">
              mada
            </span>
            <span className="font-roboto-mono text-[11px] text-[#202020]" dir="ltr">
              •••• 4821
            </span>
          </div>
          <div className="mt-3 flex items-center justify-between text-[11.5px]">
            <span className="text-[#8a857c]">{copy.payTo}</span>
            <span className="font-medium text-[#202020]">{copy.share}</span>
          </div>
          <div className="mt-4 flex flex-col items-center gap-1.5">
            <div className="relative flex h-12 w-12 items-center justify-center">
              <motion.span
                className="absolute text-[#0a84ff]"
                initial={false}
                animate={
                  step === STEP.pay
                    ? { opacity: [1, 1, 0], scale: [0.9, 1.05, 0.8] }
                    : { opacity: 1, scale: 0.9 }
                }
                transition={{ duration: 1.05, times: [0, 0.7, 1] }}
              >
                <ScanFace className="h-11 w-11" strokeWidth={1.4} />
              </motion.span>
              <CheckBadge show={step === STEP.pay} size={40} bg="#0a84ff" delay={0.85} />
            </div>
            <span className="text-[10.5px] text-[#8a857c]">{copy.confirm}</span>
          </div>
        </motion.div>

        {/* Booking pass */}
        <motion.div
          className="absolute inset-0 z-40 flex flex-col items-center bg-[#fbfaf8] px-5 pt-[64px]"
          initial={false}
          animate={{
            opacity: step === STEP.booked ? 1 : 0,
            scale: step === STEP.booked ? 1 : 1.04,
          }}
          style={{ pointerEvents: 'none' }}
          transition={{ duration: 0.45, ease: EASE_OUT_EXPO }}
        >
          <CheckBadge show={step === STEP.booked} size={52} bg={brand.accent} delay={0.15} />
          <Reveal show={step === STEP.booked} delay={0.25} className="mt-3 text-center">
            <p className="text-[20px] font-bold text-[#202020]">{copy.booked}</p>
            <p className="mt-1 text-[11.5px] text-[#8a857c]">{copy.when}</p>
          </Reveal>

          <Reveal
            show={step === STEP.booked}
            delay={0.4}
            y={18}
            className="mt-5 w-full rounded-[20px] border border-[#ebe7e1] bg-white p-4"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[13px] font-semibold text-[#202020]">{copy.court}</p>
                <p className="text-[10.5px] text-[#8a857c]">{brand.name[locale]}</p>
              </div>
              <BrandMark brand={brand} size={26} />
            </div>
            <div className="my-3 border-t border-dashed border-[#dcd7cf]" />
            <div className="flex flex-col items-center gap-2">
              <FakeQr size={96} seed={12} />
              <p className="text-[10px] text-[#8a857c]">{copy.pass}</p>
            </div>
          </Reveal>

          <Reveal
            show={step === STEP.booked}
            delay={0.6}
            className="mt-3 flex w-full items-center justify-between rounded-[14px] bg-white px-3 py-2.5 ring-1 ring-[#ebe7e1]"
          >
            <span className="text-[10.5px] font-medium text-[#202020]">{copy.invites}</span>
            <div className="flex -space-x-1 rtl:space-x-reverse">
              {PLAYERS.map((player, index) => (
                <span key={player.initials} className="relative">
                  <Avatar initials={player.initials} tone={player.tone} size={22} />
                  <span className="absolute -bottom-1 -end-1">
                    <CheckBadge
                      show={step === STEP.booked}
                      size={12}
                      bg={DEMO_COLORS.whatsapp}
                      delay={1 + index * 0.45}
                    />
                  </span>
                </span>
              ))}
            </div>
          </Reveal>

          <Reveal
            show={step === STEP.booked}
            delay={0.75}
            className="mt-auto mb-9 flex h-10 w-full items-center justify-center rounded-[14px] border border-[#dcd7cf] bg-white text-[12px] font-semibold"
          >
            <span style={{ color: brand.accent }}>{copy.calendar}</span>
          </Reveal>
        </motion.div>
      </PhoneFrame>
    </DemoStage>
  );
}
